import React, { useContext, useEffect, useState } from "react";
import { APIContext } from "../../api";
import {
  Zap,
  TrendingUp,
  CalendarDays,
  Activity,
  RefreshCw,
} from "lucide-react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);
const number = (value) => Number(value || 0).toLocaleString();
export default function UserAiUsage({ userID }) {
  const { getUserAiUsage } = useContext(APIContext);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(false);
    getUserAiUsage(userID, days)
      .then((result) => {
        if (current) setData(result);
      })
      .catch(() => {
        if (current) {
          setError(true);
          setData(null);
        }
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
    // APIContext creates functions on render; requests depend on the selected user/range.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID, days, refresh]);
  const timeline = data?.timeline || [];
  const total = data?.summary?.total_credits || 0;
  const activeDays = timeline.filter((day) => day.total_credits > 0).length;
  const period = days === "all" ? "All time" : `Last ${days} days`;
  const cards = [
    {
      label: "Lifetime AI credits",
      value: data?.summary?.lifetime_credits,
      icon: Zap,
      color: "bg-purple-50 text-[#623ce7]",
    },
    {
      label: `${period} credits`,
      value: total,
      icon: TrendingUp,
      color: "bg-blue-50 text-blue-600",
    },
    {
      label: "Daily average",
      value: Math.round(total / (timeline.length || 1)),
      icon: CalendarDays,
      color: "bg-emerald-50 text-emerald-600",
    },
    {
      label: "Active days",
      value: activeDays,
      icon: Activity,
      color: "bg-amber-50 text-amber-600",
    },
  ];
  return (
    <section
      className="space-y-4"
      aria-label="User AI usage"
      aria-busy={loading}
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-slate-900">AI Usage</h2>
          <p className="text-xs text-slate-500 mt-1">
            Credit consumption across calendar days in IST (Asia/Kolkata)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="flex bg-slate-100 p-1 rounded-lg"
            aria-label="AI usage period"
          >
            {[7, 15, 30, "all"].map((value) => (
              <button
                key={value}
                aria-pressed={days === value}
                onClick={() => setDays(value)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  days === value
                    ? "bg-[#623ce7] text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {value === "all" ? "All" : `${value}D`}
              </button>
            ))}
          </div>
          <button
            aria-label="Refresh user AI usage"
            disabled={loading}
            onClick={() => setRefresh((value) => value + 1)}
            className="p-2 rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-[#623ce7] disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>
      {data?.reporting?.note && <p className="text-xs text-slate-500 rounded-lg border border-slate-200 bg-white p-3">{data.reporting.note}</p>}
      {error ? (
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700"
        >
          AI usage could not be loaded. Use refresh to retry.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {cards.map(({ label, value, icon: Icon, color }) => (
              <div
                key={label}
                className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex justify-between items-start gap-3"
              >
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {label}
                  </p>
                  <p className="text-2xl font-bold text-slate-900 mt-2 tabular-nums">
                    {loading ? "—" : number(value)}
                  </p>
                </div>
                <div className={`p-2.5 rounded-lg ${color}`}>
                  <Icon size={20} />
                </div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 shadow-sm">
            <h3 className="text-base font-bold text-slate-900">
              AI Credit Consumption Over Time
            </h3>
            <p className="text-xs text-slate-500 mt-1 mb-6">
              {period} · daily credits consumed
              {!loading && data?.range
                ? ` · ${data.range.start_date} to ${data.range.end_date}`
                : ""}
            </p>
            <div className="h-[280px] w-full">
              {loading ? (
                <div
                  role="status"
                  className="h-full flex items-center justify-center text-sm text-slate-400"
                >
                  Loading AI usage…
                </div>
              ) : (
                <Line
                  aria-label={`${period}: ${number(
                    total
                  )} AI credits consumed across ${activeDays} active days`}
                  data={{
                    labels: timeline.map((day) => day.date),
                    datasets: [
                      {
                        label: "AI credits",
                        data: timeline.map((day) => day.total_credits),
                        borderColor: "#623ce7",
                        backgroundColor: "rgba(98, 60, 231, 0.08)",
                        fill: true,
                        tension: 0.25,
                        pointRadius: timeline.length > 60 ? 0 : 3,
                        borderWidth: 2,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: "#0f172a",
                        padding: 12,
                        cornerRadius: 8,
                        callbacks: {
                          label: (context) =>
                            `${number(context.parsed.y)} credits`,
                        },
                      },
                    },
                    scales: {
                      x: {
                        grid: { display: false },
                        ticks: {
                          color: "#64748b",
                          maxTicksLimit: 8,
                          maxRotation: 0,
                        },
                      },
                      y: {
                        beginAtZero: true,
                        grid: { color: "#f1f5f9" },
                        ticks: { color: "#64748b" },
                      },
                    },
                  }}
                />
              )}
            </div>
            {!loading && total === 0 && (
              <p className="text-sm text-slate-500 mt-4">
                No AI credits consumed in this period.
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}
